import { StyleSheet, View } from 'react-native';

import { Input } from '@/components/Input';
import { SchoolPicker } from '@/components/SchoolPicker';
import { SportPicker } from '@/components/gifting/SportPicker';
import { spacing } from '@/constants/theme';
import { isValidEmail } from '@/lib/contact';

export type AthleteProfileValues = {
  firstName: string;
  lastName: string;
  email: string;
  school: string | null;
  sport: string | null;
  instagram: string;
  tiktok: string;
};

/**
 * Which form input satisfies which server-named field. The server sends
 * `campaign_profile_missing` using ITS names, so the translation has to exist
 * somewhere — it lives here, next to the inputs, rather than in the screen.
 * Add a field server-side and only this file and the inputs below change.
 */
const SATISFIED_BY: Record<string, (v: AthleteProfileValues) => boolean> = {
  first_name: (v) => v.firstName.trim().length > 0,
  last_name: (v) => v.lastName.trim().length > 0,
  email: (v) => isValidEmail(v.email),
  school: (v) => (v.school ?? '').trim().length > 0,
  sport: (v) => v.sport !== null,
  instagram_handle: (v) => v.instagram.trim().length > 0,
};

/**
 * True when everything the SERVER asked for is filled in. Unknown field names
 * pass: a newer server asking for something this build has no input for must
 * not deadlock the button — the request will fail with a real message instead
 * of a form that can never be submitted.
 */
export function satisfies(missing: string[], values: AthleteProfileValues): boolean {
  return missing.every((field) => SATISFIED_BY[field]?.(values) ?? true);
}

type Props = {
  values: AthleteProfileValues;
  onChange: <K extends keyof AthleteProfileValues>(key: K, value: AthleteProfileValues[K]) => void;
  /** Server said this account has no email yet, so we may ask for one. */
  needsEmail: boolean;
};

export function AthleteProfileForm({ values, onChange, needsEmail }: Props) {
  return (
    <>
      <View style={styles.row}>
        <View style={styles.rowItem}>
          <Input
            label="First name"
            value={values.firstName}
            onChangeText={(t) => onChange('firstName', t)}
            autoCapitalize="words"
            autoComplete="given-name"
            textContentType="givenName"
          />
        </View>
        <View style={styles.rowItem}>
          <Input
            label="Last name"
            value={values.lastName}
            onChangeText={(t) => onChange('lastName', t)}
            autoCapitalize="words"
            autoComplete="family-name"
            textContentType="familyName"
          />
        </View>
      </View>

      {/* Shown only when the server says it's missing: an account that already
          has an email can't rebind it here (a change would make the next
          ActiveCampaign sync create a duplicate contact). */}
      {needsEmail && (
        <Input
          label="Email"
          value={values.email}
          onChangeText={(t) => onChange('email', t)}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="email"
          textContentType="emailAddress"
          placeholder="you@school.edu"
        />
      )}

      <SchoolPicker value={values.school} onSelect={(s) => onChange('school', s)} />
      <SportPicker value={values.sport} onSelect={(s) => onChange('sport', s)} />

      <Input
        label="Instagram"
        value={values.instagram}
        onChangeText={(t) => onChange('instagram', t)}
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="off"
        placeholder="@yourhandle"
      />
      <Input
        label="TikTok (optional)"
        value={values.tiktok}
        onChangeText={(t) => onChange('tiktok', t)}
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="off"
        placeholder="@yourhandle"
      />
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  rowItem: {
    flex: 1,
  },
});
